import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useSubscription } from "@/context/SubscriptionContext";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Plus, Trash2, ArrowRight, Lock, ShieldCheck } from "lucide-react";
import AttachButton from "@/components/AttachButton";
import QCRecordModal from "@/components/QCRecordModal";
import WorkflowFilters from "@/components/workflow/WorkflowFilters";
import { applyFilters, bucketCounts } from "@/lib/workflow-filters";

// Iter-136 · Universal doc detail sheet for PR / PO / GRN / Quote / SO
function DocDetailSheet({ doc, onClose }) {
  if (!doc) return null;
  const t = doc._type;
  const TITLES = { pr: "Purchase Requisition", po: "Purchase Order", grn: "Goods Receipt Note", quote: "Quote / Estimate", so: "Sales Order" };
  const numFields = { pr: "pr_number", po: "po_number", grn: "grn_number", quote: "quote_number", so: "so_number" };
  const partyLabel = t === "quote" || t === "so" ? "Customer" : "Vendor";
  const partyName = doc.customer_name || doc.vendor_name || "—";
  const lines = doc.lines || [];
  return (
    <Sheet open={!!doc} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none space-y-4 overflow-y-auto" data-testid={`doc-detail-${t}`}>
        <SheetHeader className="pb-3 border-b">
          <SheetTitle className="font-heading flex items-center gap-3">
            <span className="text-[10px] font-mono uppercase text-accent px-2 py-0.5 rounded-sm bg-accent/10">{t}</span>
            {doc[numFields[t]] || doc.id?.slice(0, 8)}
            <span className={`text-[10px] font-mono uppercase ml-auto ${doc.status === "converted" || doc.status === "invoiced" || doc.status === "billed" ? "text-emerald-600" : doc.status === "rejected" ? "text-red-500" : "text-accent"}`}>{doc.status || "—"}</span>
          </SheetTitle>
          <div className="text-xs text-muted-foreground">{TITLES[t]}</div>
        </SheetHeader>
        <div className="text-sm space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Date</div><div className="mt-1 font-mono">{fmtDate(doc.date)}</div></div>
            <div className="card-sharp p-3 col-span-2"><div className="text-[10px] font-mono uppercase text-muted-foreground">{partyLabel}</div><div className="mt-1">{partyName}</div></div>
            {doc.grand_total !== undefined && <div className="card-sharp p-3 bg-accent/5 col-span-3"><div className="text-[10px] font-mono uppercase text-accent">Grand total</div><div className="mt-1 text-2xl font-bold tabular-nums">{fmtINR(doc.grand_total || 0)}</div></div>}
            {doc.vehicle_no && <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Vehicle</div><div className="mt-1 font-mono">{doc.vehicle_no}</div></div>}
            {doc.valid_until && <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Valid until</div><div className="mt-1 font-mono">{fmtDate(doc.valid_until)}</div></div>}
            {doc.approval_status && <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Approval</div><div className={`mt-1 font-mono uppercase text-xs ${doc.approval_status === "approved" ? "text-emerald-600" : "text-accent"}`}>{doc.approval_status.replace(/_/g, " ")}</div></div>}
          </div>
          {lines.length > 0 && (
            <div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground mb-2">Line items ({lines.length})</div>
              <div className="border rounded-sm overflow-hidden"><table className="w-full text-xs"><thead className="bg-muted/40 text-[10px] font-mono uppercase text-muted-foreground"><tr><th className="p-2 text-left">Item / Description</th><th className="p-2 text-left">HSN</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">GST%</th><th className="p-2 text-right">Total</th></tr></thead><tbody>
                {lines.map((l, i) => (
                  <tr key={i} className="border-t"><td className="p-2">{l.description || l.item_name || "—"}</td><td className="p-2 font-mono text-[10px]">{l.hsn_code || "—"}</td><td className="p-2 text-right tabular-nums">{l.qty} {l.unit || ""}</td><td className="p-2 text-right tabular-nums">{fmtINR(l.rate || 0)}</td><td className="p-2 text-right tabular-nums text-[10px]">{l.gst_rate ?? "—"}</td><td className="p-2 text-right tabular-nums font-medium">{fmtINR(l.total || (l.qty || 0) * (l.rate || 0))}</td></tr>
                ))}
              </tbody></table></div>
            </div>
          )}
          {doc.linked_doc_ref && <div className="border rounded-sm p-3 bg-accent/5"><div className="text-[10px] font-mono uppercase text-accent">// Linked document</div><div className="mt-1 font-mono text-xs">{doc.linked_doc_type} · {doc.linked_doc_ref}</div></div>}
          {doc.po_number && t === "grn" && <div className="border rounded-sm p-3 bg-muted/20"><div className="text-[10px] font-mono uppercase text-muted-foreground">// Source PO</div><div className="mt-1 font-mono text-xs">{doc.po_number}</div></div>}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function LineTable({ lines, setLines, items }) {
  const set = (i, patch) => setLines(lines.map((l, j) => j === i ? { ...l, ...patch } : l));
  return (
    <div className="border rounded-sm overflow-hidden">
      <table className="w-full dense">
        <thead><tr><th style={{width:220}}>Item / Description</th><th style={{width:80}}>HSN</th><th style={{width:70}} className="text-right">Qty</th><th style={{width:100}} className="text-right">Rate</th><th style={{width:70}} className="text-right">GST%</th><th></th></tr></thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="p-1 space-y-1">
                <Select value={l.item_id || "custom"} onValueChange={(v) => { if (v === "custom") return; const it = items.find((x) => x.id === v); set(i, { item_id: v, description: it?.name || "", hsn_code: it?.hsn_code || "", rate: it?.sale_price || l.rate, gst_rate: it?.gst_rate ?? 18 }); }}>
                  <SelectTrigger className="rounded-sm h-8 text-xs"><SelectValue placeholder="Pick item" /></SelectTrigger>
                  <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto"><SelectItem value="custom">— Custom line —</SelectItem>{items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}</div></SelectContent>
                </Select>
                <Input value={l.description || ""} onChange={(e) => set(i, { description: e.target.value })} className="rounded-sm h-8 text-xs" />
              </td>
              <td className="p-1"><Input value={l.hsn_code || ""} onChange={(e) => set(i, { hsn_code: e.target.value })} className="rounded-sm h-8 text-xs" /></td>
              <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => set(i, { qty: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
              <td className="p-1"><Input type="number" value={l.rate} onChange={(e) => set(i, { rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
              <td className="p-1"><Input type="number" value={l.gst_rate ?? 18} onChange={(e) => set(i, { gst_rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" /></td>
              <td className="text-center">{lines.length > 1 && <button onClick={() => setLines(lines.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="p-2 border-t"><Button variant="ghost" size="sm" onClick={() => setLines([...lines, { qty: 1, rate: 0, gst_rate: 18 }])} className="rounded-sm gap-1"><Plus className="w-3.5 h-3.5" /> Line</Button></div>
    </div>
  );
}

export default function Workflow({ kind = "sales" }) {
  const { sub } = useSubscription();
  const flowKind = kind === "procurement" ? "purchase_flow" : "sales_flow";
  const flowSteps = sub?.flow_config?.[flowKind] || [];
  const stepEnabled = (key) => flowSteps.find((s) => s.key === key)?.enabled || false;

  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [prs, setPrs] = useState([]);
  const [grns, setGrns] = useState([]);
  const [qcTarget, setQcTarget] = useState(null); // {kind, id, ref}
  const [quotes, setQuotes] = useState([]);
  const [sos, setSos] = useState([]);
  const [drawer, setDrawer] = useState(null); // pr|grn|quote|so
  const [grnView, setGrnView] = useState(null);
  const [docView, setDocView] = useState(null); // Iter-136 · universal PR/PO/Quote/SO drawer
  const [lines, setLines] = useState([{ qty: 1, rate: 0, gst_rate: 18 }]);
  const [meta, setMeta] = useState({});
  const emptyFilter = { status: "all", from: "", to: "", party: "" };
  const [prFilter, setPrFilter] = useState(emptyFilter);
  const [poFilter, setPoFilter] = useState(emptyFilter);
  const [grnFilter, setGrnFilter] = useState(emptyFilter);
  const [quoteFilter, setQuoteFilter] = useState(emptyFilter);
  const [soFilter, setSoFilter] = useState(emptyFilter);
  const fPrs = React.useMemo(() => applyFilters(prs, "pr", prFilter), [prs, prFilter]);
  const fPos = React.useMemo(() => applyFilters(pos, "po", poFilter), [pos, poFilter]);
  const fGrns = React.useMemo(() => applyFilters(grns, "grn", grnFilter), [grns, grnFilter]);
  const fQuotes = React.useMemo(() => applyFilters(quotes, "quote", quoteFilter), [quotes, quoteFilter]);
  const fSos = React.useMemo(() => applyFilters(sos, "so", soFilter), [sos, soFilter]);

  const load = async () => {
    setItems((await api.get("/items")).data);
    setCustomers((await api.get("/parties", { params: { party_type: "customer" } })).data);
    setVendors((await api.get("/parties", { params: { party_type: "vendor" } })).data);
    if (kind === "procurement") {
      setPrs((await api.get("/purchase/requisitions")).data);
      setGrns((await api.get("/purchase/grns")).data);
      setPos((await api.get("/purchase/orders")).data);
    } else {
      setQuotes((await api.get("/sales/quotes")).data);
      setSos((await api.get("/sales/orders")).data);
    }
  };
  useEffect(() => { load(); }, [kind]);

  const open = (docType) => { setDrawer(docType); setLines([{ qty: 1, rate: 0, gst_rate: 18 }]); setMeta({}); };

  const submit = async () => {
    try {
      const body = { ...meta, lines };
      if (drawer === "pr") await api.post("/purchase/requisitions", body);
      if (drawer === "grn") await api.post("/purchase/grns", { ...body, lines: lines.map((l) => ({ ...l, qty_received: l.qty })) });
      if (drawer === "quote") await api.post("/sales/quotes", body);
      if (drawer === "so") await api.post("/sales/orders", body);
      toast.success("Created"); setDrawer(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const convertPR = async (pr) => {
    const vid = pr.vendor_hint || vendors[0]?.id;
    if (!vid) return toast.error("Add a vendor first");
    try { await api.post(`/purchase/requisitions/${pr.id}/convert-po`, { vendor_id: vid });
      toast.success("PR → PO created"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const convertPOtoGRN = async (po) => {
    try { await api.post(`/purchase/orders/${po.id}/convert-grn`, {});
      toast.success(`PO ${po.po_number} → GRN created`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const convertGRNtoBill = async (grn) => {
    try { const r = await api.post(`/purchase/grns/${grn.id}/convert-bill`);
      toast.success(`GRN ${grn.grn_number} → Bill ${r.data.bill.bill_number} posted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const convertQuoteToSO = async (q) => {
    try { await api.post(`/sales/quotes/${q.id}/convert-so`); toast.success("Quote → SO created"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const convertQuoteToInvoice = async (q) => {
    try { const r = await api.post(`/sales/quotes/${q.id}/convert-invoice`);
      toast.success(`Quote → Invoice ${r.data.invoice.invoice_number} posted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const convertSOtoInvoice = async (so) => {
    try { const r = await api.post(`/sales/orders/${so.id}/convert-invoice`);
      toast.success(`SO → Invoice ${r.data.invoice.invoice_number} posted`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const FlowStrip = () => (
    <div className="card-sharp p-3 flex flex-wrap items-center gap-2" data-testid="flow-strip">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-2">Active steps:</div>
      {flowSteps.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <ArrowRight className="w-3 h-3 text-muted-foreground" />}
          <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm ${s.enabled ? "bg-accent/10 text-accent border border-accent/30" : "text-muted-foreground/60 line-through"}`}>{s.label}</span>
        </React.Fragment>
      ))}
      {sub?.flow_locked && <div className="ml-auto flex items-center gap-1.5 text-[10px] font-mono uppercase text-accent"><Lock className="w-3 h-3" /> Locked</div>}
    </div>
  );

  if (kind === "procurement") {
    return (
      <div className="p-6 lg:p-8 space-y-6" data-testid="procurement-workflow-page">
        <PageHeader eyebrow="Procurement" title="Procure to Pay" description="PR → PO → GRN → Bill → Payment. Each step inherits data from the previous stage." />
        <FlowStrip />
        <Tabs defaultValue="pr">
          <TabsList className="rounded-sm">
            {stepEnabled("requisition") && <TabsTrigger value="pr" className="rounded-sm">Requisitions ({prs.length})</TabsTrigger>}
            <TabsTrigger value="po" className="rounded-sm">Purchase Orders ({pos.length})</TabsTrigger>
            {stepEnabled("grn") && <TabsTrigger value="grn" className="rounded-sm">GRN ({grns.length})</TabsTrigger>}
          </TabsList>
          {stepEnabled("requisition") && (
            <TabsContent value="pr" className="mt-4 space-y-3">
              <div className="flex items-end justify-between gap-2">
                <WorkflowFilters value={prFilter} onChange={setPrFilter} counts={bucketCounts(prs, "pr")} parties={vendors} testIdPrefix="pr-filter" />
                <Button onClick={() => open("pr")} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-pr-btn">+ New Requisition</Button>
              </div>
              <div className="card-sharp overflow-hidden">
                <table className="w-full dense">
                  <thead><tr><th>Date</th><th>PR #</th><th>Requester</th><th className="text-right">Amount</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {fPrs.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No requisitions match the filter</td></tr>}
                    {fPrs.map((p) => (
                      <tr key={p.id} onClick={() => setDocView({ ...p, _type: "pr" })} className="cursor-pointer hover:bg-muted/40" data-testid={`wf-pr-${p.pr_number}`}>
                        <td className="text-xs">{fmtDate(p.date)}</td>
                        <td className="font-mono text-xs">{p.pr_number}</td>
                        <td className="text-sm">{p.requester_name || "—"}</td>
                        <td className="text-right tabular-nums">{fmtINR(p.grand_total)}</td>
                        <td><span className={`text-[10px] font-mono uppercase ${p.status === "converted" ? "text-emerald-600" : "text-accent"}`}>{p.status}</span></td>
                        <td className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                          <AttachButton docType="purchase_requisition" docId={p.id} docNumber={p.pr_number} />
                          {p.status === "open" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertPR(p)} data-testid={`convert-pr-${p.pr_number}`}>→ PO <ArrowRight className="w-3 h-3" /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
          <TabsContent value="po" className="mt-4 space-y-3">
            <WorkflowFilters value={poFilter} onChange={setPoFilter} counts={bucketCounts(pos, "po")} parties={vendors} testIdPrefix="po-filter" />
            <div className="card-sharp overflow-hidden">
              <table className="w-full dense">
                <thead><tr><th>Date</th><th>PO #</th><th>Vendor</th><th className="text-right">Amount</th><th>Approval</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {fPos.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No POs match the filter</td></tr>}
                  {fPos.map((p) => (
                    <tr key={p.id} onClick={() => setDocView({ ...p, _type: "po" })} className="cursor-pointer hover:bg-muted/40" data-testid={`wf-po-${p.po_number}`}>
                      <td className="text-xs">{fmtDate(p.date)}</td>
                      <td className="font-mono text-xs">{p.po_number}</td>
                      <td>{p.vendor_name}</td>
                      <td className="text-right tabular-nums">{fmtINR(p.grand_total)}</td>
                      <td><span className={`text-[10px] font-mono uppercase ${p.approval_status === "approved" ? "text-emerald-600" : p.approval_status === "pending" ? "text-warning" : "text-muted-foreground"}`}>{(p.approval_status || "—").replace("_", " ")}</span></td>
                      <td><span className="text-[10px] font-mono uppercase">{p.status}</span></td>
                      <td className="text-right" onClick={(e) => e.stopPropagation()}>
                        {p.status === "open" && stepEnabled("grn") && (
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertPOtoGRN(p)} data-testid={`convert-po-${p.po_number}`}>→ GRN <ArrowRight className="w-3 h-3" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
          {stepEnabled("grn") && (
            <TabsContent value="grn" className="mt-4 space-y-3">
              <div className="flex items-end justify-between gap-2">
                <WorkflowFilters value={grnFilter} onChange={setGrnFilter} counts={bucketCounts(grns, "grn")} parties={vendors} testIdPrefix="grn-filter" />
                <Button onClick={() => open("grn")} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-grn-btn">+ New GRN</Button>
              </div>
              <div className="card-sharp overflow-hidden">
                <table className="w-full dense">
                  <thead><tr><th>Date</th><th>GRN #</th><th>Vendor</th><th>PO ref</th><th>Vehicle</th><th>Status</th><th></th></tr></thead>
                  <tbody>
                    {fGrns.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No goods receipts match the filter</td></tr>}
                    {fGrns.map((g) => (
                      <tr key={g.id} onClick={() => setGrnView(g)} className="cursor-pointer hover:bg-muted/40" data-testid={`grn-row-${g.grn_number}`}>
                        <td className="text-xs">{fmtDate(g.date)}</td>
                        <td className="font-mono text-xs">{g.grn_number}</td>
                        <td>{g.vendor_name}</td>
                        <td className="text-xs font-mono text-muted-foreground">{g.source_po_id?.slice(0, 8) || "—"}</td>
                        <td className="text-xs font-mono">{g.vehicle_no || "—"}</td>
                        <td><span className={`text-[10px] font-mono uppercase ${g.status === "billed" ? "text-emerald-600" : "text-accent"}`}>{g.status}</span></td>
                        <td className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                          <AttachButton docType="goods_receipt" docId={g.id} docNumber={g.grn_number} />
                          <Button size="sm" variant="ghost" className="h-7 text-xs gap-1"
                                    onClick={() => setQcTarget({ kind: "grn_inward", id: g.id, ref: g.grn_number })}
                                    data-testid={`qc-grn-${g.grn_number}`}>
                            <ShieldCheck className="w-3 h-3" /> QC
                          </Button>
                          {g.status !== "billed" && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertGRNtoBill(g)} data-testid={`convert-grn-${g.grn_number}`}>→ Bill <ArrowRight className="w-3 h-3" /></Button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          )}
        </Tabs>

        <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
          <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto space-y-3">
            <SheetHeader><SheetTitle className="font-heading">{drawer === "pr" ? "New Purchase Requisition" : "New Goods Receipt Note"}</SheetTitle></SheetHeader>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div><Label>Date</Label><Input type="date" value={meta.date || today()} onChange={(e) => setMeta({ ...meta, date: e.target.value })} className="rounded-sm mt-1" /></div>
              {drawer === "pr" && <div><Label>Requester</Label><Input value={meta.requester_name || ""} onChange={(e) => setMeta({ ...meta, requester_name: e.target.value })} className="rounded-sm mt-1" data-testid="pr-requester" /></div>}
              {drawer === "grn" && (<><div><Label>Vendor</Label><Select value={meta.vendor_id} onValueChange={(v) => setMeta({ ...meta, vendor_id: v })}><SelectTrigger className="rounded-sm mt-1" data-testid="grn-vendor"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>PO reference</Label><Select value={meta.source_po_id} onValueChange={(v) => setMeta({ ...meta, source_po_id: v })}><SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="Optional PO" /></SelectTrigger><SelectContent>{pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.po_number}</SelectItem>)}</SelectContent></Select></div>
                <div className="col-span-2"><Label>Vehicle #</Label><Input value={meta.vehicle_no || ""} onChange={(e) => setMeta({ ...meta, vehicle_no: e.target.value })} className="rounded-sm mt-1 font-mono" /></div></>)}
              {drawer === "pr" && <div className="col-span-2"><Label>Reason</Label><Input value={meta.reason || ""} onChange={(e) => setMeta({ ...meta, reason: e.target.value })} className="rounded-sm mt-1" placeholder="What is this for?" /></div>}
            </div>
            <LineTable lines={lines} setLines={setLines} items={items} />
            <Button onClick={submit} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="wf-submit">Create</Button>
          </SheetContent>
        </Sheet>
      </div>
    );
  }

  // ---------------- Sales / Order-to-Cash side ----------------
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="sales-workflow-page">
      <PageHeader eyebrow="Sales" title="Order to Cash" description="Quote → Sales Order → Invoice → Receipt. Each step auto-inherits customer &amp; lines from the previous document." />
      <FlowStrip />

      <Tabs defaultValue={stepEnabled("quote") ? "quote" : "so"}>
        <TabsList className="rounded-sm">
          {stepEnabled("quote") && <TabsTrigger value="quote" className="rounded-sm">Quotes ({quotes.length})</TabsTrigger>}
          {stepEnabled("sales_order") && <TabsTrigger value="so" className="rounded-sm">Sales Orders ({sos.length})</TabsTrigger>}
        </TabsList>

        {stepEnabled("quote") && (
          <TabsContent value="quote" className="mt-4 space-y-3">
            <div className="flex items-end justify-between gap-2">
              <WorkflowFilters value={quoteFilter} onChange={setQuoteFilter} counts={bucketCounts(quotes, "quote")} parties={customers} testIdPrefix="quote-filter" />
              <Button onClick={() => open("quote")} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-quote-btn">+ New Quote</Button>
            </div>
            <div className="card-sharp overflow-hidden">
              <table className="w-full dense">
                <thead><tr><th>Date</th><th>Quote #</th><th>Customer</th><th className="text-right">Amount</th><th>Valid until</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {fQuotes.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">No quotes match the filter</td></tr>}
                  {fQuotes.map((q) => (
                    <tr key={q.id} onClick={() => setDocView({ ...q, _type: "quote" })} className="cursor-pointer hover:bg-muted/40" data-testid={`wf-quote-${q.quote_number}`}>
                      <td className="text-xs">{fmtDate(q.date)}</td>
                      <td className="font-mono text-xs">{q.quote_number}</td>
                      <td>{q.customer_name}</td>
                      <td className="text-right tabular-nums">{fmtINR(q.grand_total)}</td>
                      <td className="text-xs">{q.valid_until ? fmtDate(q.valid_until) : "—"}</td>
                      <td><span className={`text-[10px] font-mono uppercase ${q.status === "converted" || q.status === "invoiced" ? "text-emerald-600" : "text-accent"}`}>{q.status}</span></td>
                      <td className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                        <AttachButton docType="quote" docId={q.id} docNumber={q.quote_number} />
                        {q.status === "open" && stepEnabled("sales_order") && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertQuoteToSO(q)} data-testid={`convert-quote-so-${q.quote_number}`}>→ SO <ArrowRight className="w-3 h-3" /></Button>}
                        {q.status === "open" && !stepEnabled("sales_order") && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertQuoteToInvoice(q)} data-testid={`convert-quote-inv-${q.quote_number}`}>→ Invoice <ArrowRight className="w-3 h-3" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}

        {stepEnabled("sales_order") && (
          <TabsContent value="so" className="mt-4 space-y-3">
            <div className="flex items-end justify-between gap-2">
              <WorkflowFilters value={soFilter} onChange={setSoFilter} counts={bucketCounts(sos, "so")} parties={customers} testIdPrefix="so-filter" />
              <Button onClick={() => open("so")} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-so-btn">+ New Sales Order</Button>
            </div>
            <div className="card-sharp overflow-hidden">
              <table className="w-full dense">
                <thead><tr><th>Date</th><th>SO #</th><th>Customer</th><th className="text-right">Amount</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {fSos.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-muted-foreground">No sales orders match the filter</td></tr>}
                  {fSos.map((s) => (
                    <tr key={s.id} onClick={() => setDocView({ ...s, _type: "so" })} className="cursor-pointer hover:bg-muted/40" data-testid={`wf-so-${s.so_number}`}>
                      <td className="text-xs">{fmtDate(s.date)}</td>
                      <td className="font-mono text-xs">{s.so_number}</td>
                      <td>{s.customer_name}</td>
                      <td className="text-right tabular-nums">{fmtINR(s.grand_total)}</td>
                      <td><span className={`text-[10px] font-mono uppercase ${s.status === "invoiced" ? "text-emerald-600" : "text-accent"}`}>{s.status}</span></td>
                      <td className="text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                        <AttachButton docType="sales_order" docId={s.id} docNumber={s.so_number} />
                        {!s.converted_invoice_id && <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => convertSOtoInvoice(s)} data-testid={`convert-so-${s.so_number}`}>→ Invoice <ArrowRight className="w-3 h-3" /></Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Sheet open={!!drawer} onOpenChange={(o) => !o && setDrawer(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto space-y-3">
          <SheetHeader><SheetTitle className="font-heading">{drawer === "quote" ? "New Quote / Estimate" : "New Sales Order"}</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div><Label>Customer</Label><Select value={meta.customer_id} onValueChange={(v) => setMeta({ ...meta, customer_id: v })}><SelectTrigger className="rounded-sm mt-1" data-testid={`${drawer}-customer`}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Date</Label><Input type="date" value={meta.date || today()} onChange={(e) => setMeta({ ...meta, date: e.target.value })} className="rounded-sm mt-1" /></div>
            {drawer === "quote" && <div className="col-span-2"><Label>Valid until</Label><Input type="date" value={meta.valid_until || ""} onChange={(e) => setMeta({ ...meta, valid_until: e.target.value })} className="rounded-sm mt-1" /></div>}
          </div>
          <LineTable lines={lines} setLines={setLines} items={items} />
          <Button onClick={submit} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="wf-submit">{drawer === "quote" ? "Create Quote" : "Create Sales Order"}</Button>
        </SheetContent>
      </Sheet>

      {qcTarget && (
        <QCRecordModal
          open={!!qcTarget}
          onOpenChange={(o) => !o && setQcTarget(null)}
          kind={qcTarget.kind}
          sourceId={qcTarget.id}
          sourceRef={qcTarget.ref}
          onCreated={() => load()}
        />
      )}

      <DocDetailSheet doc={docView} onClose={() => setDocView(null)} />
      <DocDetailSheet doc={grnView && { ...grnView, _type: "grn" }} onClose={() => setGrnView(null)} />
    </div>
  );
}
